import { Command } from 'commander';
import chalk from 'chalk';
import boxen from 'boxen';
import { ConfigService } from '../../services/config.service.js';
import { trackActionSync } from '../../services/tracking.service.js';
import { ValidationError } from '../../errors/index.js';
import type { InitOptions } from '../../types/framework.types.js';
import { getCliName } from '../../utils/cli-name.js';

/**
 * Create auth command for Jai1 Framework
 * Allows both interactive and non-interactive modes
 */
export function createAuthCommand(): Command {
  const cmd = new Command('auth')
    .description('Xác thực và cấu hình client')
    .option('--api-url <url>', 'Jai1 Store API URL')
    .option('--access-key <key>', 'Your access key')
    .action(async (options: InitOptions) => {
      await handleAuth(options);
    });

  return cmd;
}

async function handleAuth(options: InitOptions): Promise<void> {
  const configService = new ConfigService();

  let apiUrl: string;
  let accessKey: string;
  let isInteractive = false;

  // Check if config already exists
  const existingConfig = await configService.load();
  if (existingConfig) {
    console.log(
      chalk.yellow('⚠️  Cấu hình đã tồn tại tại:'),
      chalk.dim(configService.getConfigPath())
    );
    console.log(
      chalk.dim('    Chạy lại lệnh này với giá trị mới để ghi đè.\n')
    );
  }

  // Interactive vs Non-Interactive mode
  if (options.apiUrl && options.accessKey) {
    // Non-interactive mode
    apiUrl = options.apiUrl;
    accessKey = options.accessKey;
  } else if (options.apiUrl || options.accessKey) {
    // Partial options provided - error
    throw new ValidationError(
      'Cần cả --api-url và --access-key trong non-interactive mode'
    );
  } else {
    // Interactive mode
    isInteractive = true;

    console.log(
      boxen(chalk.cyan.bold('🚀 Jai1 Client Setup'), {
        padding: { left: 1, right: 1, top: 0, bottom: 0 },
        borderStyle: 'round',
        borderColor: 'cyan',
      })
    );
    console.log();

    // For now, use simple readline until inquirer is installed
    const readline = await import('readline');
    const rl = readline.createInterface({
      input: process.stdin,
      output: process.stdout,
    });

    const question = (prompt: string): Promise<string> => {
      return new Promise((resolve) => {
        rl.question(prompt, (answer) => {
          resolve(answer);
        });
      });
    };

    apiUrl = await question(
      chalk.dim('API URL') + chalk.gray(' (https://store.jai1.io): ')
    );
    if (!apiUrl) {
      apiUrl = 'https://store.jai1.io';
    }

    accessKey = await question(chalk.dim('Access Key: '));
    if (!accessKey) {
      rl.close();
      throw new ValidationError('Access key là bắt buộc');
    }

    rl.close();
  }

  // Validate API URL format
  try {
    new URL(apiUrl);
  } catch {
    throw new ValidationError('Định dạng API URL không hợp lệ');
  }

  console.log(chalk.dim('\nĐang xác thực access key...'));

  // TODO: Validate access key with API
  // For now, just save it
  // const isValid = await validateAccessKey(apiUrl, accessKey);
  // if (!isValid) {
  //   throw new AuthenticationError('Invalid access key');
  // }

  // Save config
  await configService.save({
    apiUrl,
    accessKey,
    version: null,
    lastUpdated: null,
  });

  // Track auth action (sync to ensure it completes before exit)
  await trackActionSync('auth', {
    configPath: configService.getConfigPath(),
    mode: isInteractive ? 'interactive' : 'non-interactive',
    isReconfig: !!existingConfig,
  });

  console.log(
    chalk.green('✓ Cấu hình đã lưu tại'),
    chalk.dim(configService.getConfigPath())
  );
  console.log();
  console.log(
    chalk.dim('Bạn đã sẵn sàng! Chạy'),
    chalk.cyan(`'${getCliName()} check'`),
    chalk.dim('để kiểm tra cập nhật.')
  );
}
